/**
 * The ONE composer (#1069 UI-3).
 *
 * Every conversation action in the panel happens here, at the bottom, in both
 * the thread-list view and the thread view. Before this, composition happened
 * in two places, in two shapes, at opposite ends of the panel: a "New conversation"
 * button at the TOP of the list that swapped for a title-only card, and a
 * message box that existed only inside an open thread.
 *
 * It is deliberately ONE component with four modes rather than several
 * components that look alike — the presentation is shared and only the submit
 * branches:
 *
 *   idle    thread list, not composing  -> the collapsed "New conversation" affordance
 *   compose thread list, composing      -> confidentiality notice + TITLE + MESSAGE
 *   reply   a thread is open            -> confidentiality notice + MESSAGE only
 *   edit    correcting your own message -> "Editing your message" strip + MESSAGE,
 *                                          Cancel and Save (Story 1069-1.3, D2)
 *
 * Editing happens HERE and not in an inline box on the row, because spec §4.3
 * says every conversation action happens at the bottom of the panel, and a
 * second composer on screen reopens the "two places, two shapes" problem this
 * one closed. The panel prefills the field with the message's text projection.
 *
 * The panel renders it OUTSIDE the `openThread ? … : …` branch, so it is one
 * instance that survives the view swap rather than two that remount — which is
 * what lets a draft outlive landing in a freshly created thread.
 *
 * The confidentiality notice is present in BOTH composing modes and tracks the
 * space live, because the one mistake this feature cannot undo is posting to
 * the wrong space, and the composer is where that mistake is made.
 *
 * It speaks in TWO VOICES, chosen by `canSeeInternal` — see the prop and the
 * `consequence` ternary. The panel's contract is that "external users see no
 * evidence the internal one exists", and a notice that names the space, or
 * calls the reader "the customer", breaks it from inside the composer.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  PanResponder,
  Platform,
} from 'react-native';

import { colors } from '../../theme/colors';
import { Lock, Paperclip, Plus, Users } from '../icons';
import {
  clampComposerHeight,
  composerAutoGrowMaxHeight,
  composerMaxHeight,
  COMPOSER_DRAG_ACTIVATION_DY,
  COMPOSER_MIN_HEIGHT,
  COMPOSER_RESIZE_COARSE_MULTIPLIER,
  COMPOSER_RESIZE_STEP,
} from './composerLayout';
import {
  MESSAGE_TOO_MUCH_FORMATTING,
  isMessageHtmlTooLarge,
  messageTextLength,
  referencedInlineIds,
} from './messageHtml';
import {
  MAX_MESSAGE_LENGTH,
  MAX_TITLE_LENGTH,
} from './providers/provider.types';
import { GRIP_TARGET, ResizeGrip } from './ResizeGrip';
import { MAY_ALREADY_BE_SENT, StagedFileStrip } from './StagedFileStrip';
import { MAX_FILES_PER_MESSAGE } from '../../services/correspondence/messageFileLimits';
import { rewriteHtmlPreviewSrcsToPendingRefs } from '../../services/pendingInlineImagePreview';
import {
  hasInlineImageUploadsInFlight,
  subscribeInlineImageUploadsInFlight,
} from '../../utils/richContent/inlineAttachmentName';
import { TextAreaEditor } from '../textArea/TextAreaEditor';

import type { ResizeGripKeyEvent } from './ResizeGrip';
import type { StagedFile } from '../../services/correspondence/stagedFile';
import type {
  InlineImageResolution,
  ResolveInlineImage,
} from '../textArea/inlineImage.types';
import type { LayoutChangeEvent } from 'react-native';

/**
 * The confidentiality notice's icon and the gap beside it. Named because the
 * second notice line indents past both to sit under the text, and a bare `17`
 * there silently stops lining up the moment either value moves.
 */
const NOTICE_ICON_SIZE = 12;
const NOTICE_GAP = 5;

/**
 * ⚠️ THE PILL AND THE TARGET NOW LIVE IN `ResizeGrip.tsx` (#1069 Story 7.2,
 * D3). Both edges draw from the one set of constants, which is what stops the
 * two grips diverging again the way they did between 2026-09-08 and -15.
 *
 * ── The slop's DIRECTION is re-derived, not carried over (D1) ────────────────
 *
 * It used to go UPWARD only, because downward it reached into the message
 * field's own top edge and an ambiguous band over a text input is the worse
 * trade. The grip has moved to the **card's** top edge, so both neighbours are
 * different neighbours and the old answer does not survive its own reasoning:
 *
 *  - UPWARD is now the MESSAGE LIST, whose bubbles are pressable — a long-press
 *    menu on native (Story 2.5) and a press target on web. Slop there would
 *    take taps off the last bubble's bottom edge.
 *  - DOWNWARD is the card's own 12px padding and, past it, the confidentiality
 *    notice: an icon and a `Text`, neither of them interactive.
 *
 * So it goes DOWNWARD only, into the inert band, for the same reason it used to
 * go upward: slop may not overlap something that responds to a tap.
 */
const resizeHitSlop = {
  top: 0,
  /**
   * ⚠️ `GRIP_TARGET / 2`, NOT `GRIP_TARGET_SLOP` (#1069 Story 8.6, D3, applied
   * at code review 2026-09-22). Story 8.6 lifted the target half of itself so
   * the pill's centre line lands on the card's border, which puts 12 of its 24
   * points OUTSIDE the card. A child drawn outside its parent's bounds is not
   * hit-tested on either native platform (UIKit's `hitTest:` returns nil
   * outside `self.bounds` and `RCTView` does not override it to admit
   * out-of-bounds subviews; Android's `ViewGroup` can only dispatch a touch its
   * own bounds already accepted), so the in-bounds half plus 8 would have been
   * a 20pt band on phones, under the 24 of SC 2.5.8 that Story 7.2 D5 exists to
   * win, with the visible pill's upper half dead.
   *
   * The story wrote this as conditional on a device measurement (D3). The
   * measurement has not been taken, and shipping 8 while it is missing ASSERTS
   * that the constraint does not hold, which is the less likely reading and the
   * unsafe default. So it is applied now and the walk confirms it rather than
   * authorising it.
   *
   * ⚠️ AND IT REACHES LESS FAR DOWN THAN THE SLOP IT REPLACES, which is why no
   * new neighbour is at risk. Measured from the card's top border: the old
   * target ended at +22 and 8 of slop reached +30; the new one ends at +12 and
   * 12 of slop reaches +24, which is the end of the card's own 12pt padding.
   * The direction rule below is unchanged and still binding.
   */
  bottom: GRIP_TARGET / 2,
  left: 0,
  right: 0,
} as const;

export type ComposerMode = 'idle' | 'compose' | 'reply' | 'edit';

/** The hints under the field. Exported so tests match the copy. */
/**
 * A stable empty array: a `[]` default in the destructure is a NEW array every
 * render, which would make `stagedFiles` a fresh prop identity each time and
 * defeat any memoisation downstream.
 */
// Frozen for the same reason as the panel's `EMPTY_STAGED`, which the run-3
// hardening reached and this one did not (code review run 4): one shared
// instance handed to every render with nothing staged, where a single in-place
// mutation would cross threads.
const EMPTY_STAGED_FILES: readonly StagedFile[] = Object.freeze([]);

/** No-op for a composer rendered without a removal handler. */
const noop = () => {};

export const MESSAGE_REQUIRED_HINT =
  'Write a message to send with this subject.';
export const BODY_TOO_LONG_HINT = `Messages are limited to ${MAX_MESSAGE_LENGTH.toLocaleString(
  'en-US',
)} characters.`;
export const EDITING_STRIP_TEXT = 'Editing your message';
/** The server's markup-ceiling sentence (#1530), shown before any upload. */
export const BODY_TOO_MUCH_FORMATTING_HINT = `${MESSAGE_TOO_MUCH_FORMATTING}.`;
/**
 * The in-flight key the editor and the panel's resolver both count under
 * (#1530), so `hasInlineImageUploadsInFlight` is true from the moment an
 * image is handed to the resolver until the editor has inserted it. One key
 * for the one composer: there is never more than one on screen.
 */
export const COMPOSER_INLINE_IMAGE_FIELD_KEY = 'correspondence:composer';

const refuseInlineImage: ResolveInlineImage =
  async (): Promise<InlineImageResolution> => ({
    ok: false,
    error: 'Open a conversation to add a picture.',
  });

/** Minimal shape of a pointer event, so the drag is testable without a DOM. */
export interface ComposerPointerEvent {
  pointerId: number;
  button: number;
  clientY: number;
  preventDefault: () => void;
}

/** Minimal shape of the handle node — see {@link attachComposerResizeDrag}. */
export interface ComposerPointerTarget {
  addEventListener: (
    type: string,
    listener: (event: ComposerPointerEvent) => void,
  ) => void;
  removeEventListener: (
    type: string,
    listener: (event: ComposerPointerEvent) => void,
  ) => void;
  setPointerCapture?: (pointerId: number) => void;
  releasePointerCapture?: (pointerId: number) => void;
  focus?: () => void;
  style?: Record<string, string>;
}

/**
 * Pointer drag for the composer's resize handle (web).
 *
 * Deliberately the same shape as `CorrespondenceDock`'s `attachDockResizeDrag`
 * — a node-shaped interface rather than an `HTMLElement`, so the gesture's
 * LOGIC is reachable from a test that renders through the React Native test
 * renderer, where the ref is not a DOM node at all. That drag is horizontal,
 * web-only and already shipped, so it is copied as a pattern rather than
 * called: this one is VERTICAL and the two share no state.
 *
 * `getHeight` is read at pointerdown rather than passed in, so the gesture
 * starts from whatever height is on screen — including one auto-grow or the
 * keyboard just produced.
 *
 * Returns its own teardown.
 */
export const attachComposerResizeDrag = (
  node: ComposerPointerTarget,
  getHeight: () => number,
  onResize: (next: number) => void,
): (() => void) => {
  if (node.style) {
    node.style.cursor = 'row-resize';
    node.style.userSelect = 'none';
    // Without this, touch scrolling claims the gesture before we see a move.
    node.style.touchAction = 'none';
  }

  let activePointer: number | null = null;
  let startY = 0;
  let startHeight = 0;
  /**
   * Whether this gesture has actually MOVED the field yet. Only a drag that
   * committed something has anything to revert, and a `pointercancel` that
   * arrives before the first move must not pin a field that was still
   * auto-growing.
   */
  let moved = false;

  const onPointerDown = (event: ComposerPointerEvent) => {
    // Primary button only, and one pointer at a time: a second finger must not
    // hijack a drag already in progress.
    if (event.button !== 0 || activePointer !== null) return;
    event.preventDefault();
    // preventDefault suppresses the browser's own focus-on-mousedown, so focus
    // has to be taken explicitly — otherwise a mouse user who drags the handle
    // cannot then fine-tune with the arrow keys and never sees the focus ring.
    node.focus?.();
    activePointer = event.pointerId;
    startY = event.clientY;
    startHeight = getHeight();
    moved = false;
    node.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: ComposerPointerEvent) => {
    if (activePointer === null || event.pointerId !== activePointer) return;
    // The same activation threshold the native path enforces. Without it the
    // pointer jitter in an ordinary CLICK sets `moved`, calls `onResize`, and
    // pins the field — switching auto-grow off for the rest of the session
    // because the user tapped the grip.
    if (
      !moved &&
      Math.abs(event.clientY - startY) <= COMPOSER_DRAG_ACTIVATION_DY
    ) {
      return;
    }
    moved = true;
    // Top edge: dragging UP (a DECREASING clientY) makes the field taller.
    onResize(startHeight - (event.clientY - startY));
  };

  const release = () => {
    if (activePointer !== null) node.releasePointerCapture?.(activePointer);
    activePointer = null;
    moved = false;
  };

  /** A COMPLETED drag: the interim height is the height the user chose. */
  const endDrag = (event: ComposerPointerEvent) => {
    if (activePointer === null || event.pointerId !== activePointer) return;
    release();
  };

  /**
   * A CANCELLED drag, which is not the same thing and must not commit.
   *
   * `pointercancel` fires when the browser or the OS takes the gesture away —
   * a system gesture, the page being scrolled by something else, the touch
   * being reinterpreted. Landing that in the same path as a completed drag
   * leaves the field pinned at whatever height the interruption happened to
   * catch it at, which the user never chose and now has to undo by hand.
   *
   * `lostpointercapture` is here too because implicit capture loss (a context
   * menu, the node being removed mid-drag) is an interruption by the same
   * reasoning. After a normal `pointerup` it is a no-op: `endDrag` has already
   * cleared `activePointer`, so the guard returns.
   *
   * Reverting restores the height the drag STARTED from. It does not restore
   * "undragged" — a cancelled drag that moved still counts as having taken
   * hold of the handle. See `deferred-work.md`.
   */
  const cancelDrag = (event: ComposerPointerEvent) => {
    if (activePointer === null || event.pointerId !== activePointer) return;
    if (moved) onResize(startHeight);
    release();
  };

  node.addEventListener('pointerdown', onPointerDown);
  node.addEventListener('pointermove', onPointerMove);
  node.addEventListener('pointerup', endDrag);
  node.addEventListener('pointercancel', cancelDrag);
  // Capture can also be lost implicitly — a context menu, the node being
  // removed, a browser interrupt. Without this the drag would stay "active"
  // with no pointer held, and the next stray move over the handle would resize.
  node.addEventListener('lostpointercapture', cancelDrag);

  return () => {
    node.removeEventListener('pointerdown', onPointerDown);
    node.removeEventListener('pointermove', onPointerMove);
    node.removeEventListener('pointerup', endDrag);
    node.removeEventListener('pointercancel', cancelDrag);
    node.removeEventListener('lostpointercapture', cancelDrag);
    /**
     * TEARDOWN MID-DRAG IS AN INTERRUPTION, NOT A COMPLETION.
     *
     * The panel can close, or this effect can re-run, while a pointer is still
     * down — and until this reverted, that ALONE among the interruption paths
     * committed whatever interim height it happened to catch. `pointercancel`,
     * `lostpointercapture` and the native `onPanResponderTerminate` all revert;
     * a drag the component itself tore down out from under the user is not more
     * deliberate than any of those. Consistency here is the whole point: the
     * user should not have to know which kind of interruption they got.
     *
     * Same `moved` guard as `cancelDrag`, for the same reason — a teardown
     * before the first move has nothing to revert, and calling `onResize` there
     * would PIN a field that was still auto-growing.
     */
    if (activePointer !== null) {
      if (moved) onResize(startHeight);
      // Release a capture still held at teardown.
      node.releasePointerCapture?.(activePointer);
      activePointer = null;
      moved = false;
    }
    if (node.style) {
      node.style.cursor = '';
      node.style.userSelect = '';
      node.style.touchAction = '';
    }
  };
};

/** The slice of `PanResponderGestureState` this gesture reads. */
export interface ComposerGestureState {
  dx: number;
  dy: number;
  /**
   * How many fingers are currently down. Optional because a test driving this
   * config directly cares about the delta and not the touch count, and because
   * the single-touch case is the one every other guard is written against —
   * absent therefore reads as ONE.
   */
  numberActiveTouches?: number;
}

/**
 * Touch drag for the composer's resize handle (native).
 *
 * Returned as a plain config object rather than a `PanResponder` instance for
 * two reasons. It is the half that carries the behaviour, so it can be driven
 * directly by a test — `PanResponder.create` is globally mocked in
 * `src/setupTests.ts` to return FIXED handlers that ignore whatever it is
 * given, so a component-level assertion on this gesture would be asserting
 * against the harness. And it keeps the platform difference down to "who
 * produces the delta", which is the whole reason the clamp is shared.
 *
 * ⚠️ "SCROLL WINS ON AMBIGUITY" WAS THIS BLOCK'S RULE UNTIL 2026-09-18 AND IS
 * NOW REVERSED. It said the responder "never claims on touch-down, so a flick
 * that merely began on the grip still scrolls". Good rule; unimplementable. See
 * the long note on `onStartShouldSetPanResponder` below: a view beneath the
 * current responder is skipped by React Native's move negotiation entirely, so
 * declining at touch-down meant never being asked again. The native drag did
 * nothing at all, measured on iOS.
 *
 * WHAT THE GESTURE DOES NOW:
 *  - it CLAIMS on touch-down, on a single touch, because the grip is a 24px
 *    control whose only purpose is to be dragged. A flick beginning on it
 *    resizes rather than scrolls, and that is the trade;
 *  - {@link COMPOSER_DRAG_ACTIVATION_DY} and the more-vertical-than-horizontal
 *    test survive on the MOVE handler, which still runs as a second chance when
 *    the touch-down claim was declined (two fingers), and still keeps clear of
 *    the horizontal dismissal swipe UI-5 owns;
 *  - it grants termination on request, so a scroller can always take it back;
 *  - and having granted it, {@link ComposerGestureState.numberActiveTouches}
 *    is what keeps a SECOND finger from moving the field. `PanResponder`'s
 *    `dy` is the centroid's, so a second touch anywhere on screen shifts it and
 *    the field would jump by half the distance between the two fingers. The
 *    web path has always guarded this (one `activePointer` at a time); this is
 *    the native half of the same guard.
 *
 * The travel spent reaching the activation threshold is subtracted at grant, or
 * the field would jump by that much the instant the drag was claimed. The same
 * subtraction is re-baselined when a second finger LIFTS, because the centroid
 * jumps back just as hard as it jumped away.
 *
 * TERMINATION IS NOT COMPLETION. `onPanResponderTerminate` fires when something
 * else took the gesture — the scroller this responder deliberately yields to,
 * or the OS. That is a cancel, so it reverts to the height the drag started
 * from rather than committing whatever the interruption caught it at.
 */
export const createComposerDragResponder = (
  getHeight: () => number,
  onResize: (next: number) => void,
) => {
  let startHeight = 0;
  let startDy = 0;
  let moved = false;
  let multiTouch = false;

  const touchesDown = (gesture: ComposerGestureState): number =>
    gesture.numberActiveTouches ?? 1;

  return {
    /**
     * ⚠️ CLAIMS ON TOUCH-DOWN, AND THIS REVERSES THE RULE THE BLOCK ABOVE USED
     * TO STATE ("scroll wins on ambiguity"). Measured on an iOS simulator,
     * 2026-09-18, with the probes this reversal was found by.
     *
     * The old config declined here and claimed on MOVE instead, so that a flick
     * beginning on the grip still scrolled. That is a good rule and it could
     * not work, because of how React Native negotiates a responder on a move:
     * it computes the lowest common ancestor of the CURRENT RESPONDER and the
     * touch target, then dispatches the "should set" negotiation from there
     * SKIPPING the responder itself. A view BELOW the current responder is
     * therefore never in the path at all -- neither capture nor bubble.
     *
     * So once any ancestor took the responder at touch-down, this grip could
     * never be asked again. Observed exactly: `onStartShouldSetPanResponder`
     * fired, 52 `onTouchMove`s followed on the grip itself, and
     * `onMoveShouldSetPanResponder` and its Capture variant were never called
     * once. No `onTouchCancel` either -- nothing "stole" the gesture, the
     * negotiation simply routed around us.
     *
     * ⚠️ THIS IS NOT A REGRESSION FROM MOVING THE GRIP TO THE CARD. The
     * ancestor chain above the composer card is the same either way, and this
     * config is unchanged from before. The native drag has very probably never
     * worked, which is consistent with the deferred entry that said in as many
     * words that nobody had looked at native.
     *
     * WHAT IT COSTS: a flick that BEGINS on the grip no longer scrolls the
     * message list; it resizes. That is the correct trade for a 24px control
     * whose only purpose is to be dragged, and it is what every platform's
     * resize handle does. Everything else the old rule bought is kept: the
     * single-touch guard is enforced here instead of on move,
     * `onPanResponderTerminationRequest` still grants termination so a scroller
     * can take it back, and a terminated drag still reverts.
     */
    onStartShouldSetPanResponder: (
      _event: unknown,
      gesture: ComposerGestureState,
    ) => touchesDown(gesture) === 1,
    onMoveShouldSetPanResponder: (
      _event: unknown,
      gesture: ComposerGestureState,
    ) =>
      touchesDown(gesture) === 1 &&
      Math.abs(gesture.dy) > COMPOSER_DRAG_ACTIVATION_DY &&
      Math.abs(gesture.dy) > Math.abs(gesture.dx),
    onPanResponderTerminationRequest: () => true,
    onPanResponderGrant: (_event: unknown, gesture: ComposerGestureState) => {
      startHeight = getHeight();
      startDy = gesture.dy;
      moved = false;
      multiTouch = false;
    },
    onPanResponderMove: (_event: unknown, gesture: ComposerGestureState) => {
      /**
       * `!== 1`, MATCHING THE GRANT GUARD ABOVE, not the `> 1` this used to be.
       *
       * The two guards disagreed: the grant would only claim a gesture at
       * EXACTLY one touch, while the move handler only distrusted MORE than
       * one — so a zero-touch gesture state (the responder system reporting a
       * lift in the same frame as a move, which is what a fast flick-and-release
       * produces) sailed through the move guard and moved the field on a delta
       * belonging to no finger at all. "Exactly one dragging finger" is the
       * condition every delta in here assumes; both guards now say so.
       */
      if (touchesDown(gesture) !== 1) {
        // The centroid is no longer the dragging finger, so every delta from
        // here is a lie. Sit still until exactly one finger is down again.
        multiTouch = true;
        return;
      }
      if (multiTouch) {
        // It lifted. Re-baseline against where the remaining finger actually
        // is, or the centroid's snap back would land on the field as a jump.
        multiTouch = false;
        startHeight = getHeight();
        startDy = gesture.dy;
        return;
      }
      moved = true;
      // Dragging UP is a NEGATIVE dy and makes the field taller.
      onResize(startHeight - (gesture.dy - startDy));
    },
    onPanResponderRelease: () => {
      // A completed drag: the interim height IS the chosen height. Nothing to
      // do but stop treating the gesture as live.
      moved = false;
      multiTouch = false;
    },
    onPanResponderTerminate: () => {
      if (moved) onResize(startHeight);
      moved = false;
      multiTouch = false;
    },
  };
};

interface Props {
  mode: ComposerMode;
  /** The space ON SCREEN. Drives the notice, which must not lag the tabs. */
  internal: boolean;
  /**
   * Whether the VIEWER can reach the internal space at all — not which space
   * is on screen (`internal` above is that). It is the panel's own
   * `canSeeInternal`, and it is what decides WHICH VOICE the notice speaks in.
   *
   * Without it the composer could not tell who it was talking to, so it spoke
   * the internal voice to everyone: it told a customer they were writing "in
   * the shared space" (shared as opposed to what?) and that "the customer will
   * see this message" — third person, to the customer, which discloses that
   * there is a first person they are not part of. Neither string is a data
   * leak; both are exactly the inference the two-space design exists to
   * prevent (#1069 UI-6).
   */
  canSeeInternal: boolean;
  title: string;
  onChangeTitle: (value: string) => void;
  body: string;
  onChangeBody: (value: string) => void;
  /** A submit is in flight; the buttons go inert so two taps cannot both land. */
  submitting: boolean;
  onStartCompose: () => void;
  onCancelCompose: () => void;
  /** Send (compose), Post (reply) or Save (edit): the mode decides. */
  onSubmit: () => void;
  /**
   * Cancel an edit (mode `edit`): the panel restores the draft that was
   * there before the edit began. Required by the edit mode; ignored otherwise.
   */
  onCancelEdit?: () => void;
  /**
   * Whether the panel is open. A drag is deliberately SESSION-scoped: the
   * height resets every time the panel opens, because the conversation is the
   * point and the composer is a tool you reach for. Optional so the component
   * stands on its own; a Composer nobody tells about the panel simply never
   * resets, which is the behaviour a standalone render wants.
   */
  panelOpen?: boolean;
  /**
   * The height of the ONE container that holds the message list and this
   * composer (#1069 UI-8), measured by the panel's single `onLayout`.
   *
   * ⚠️ READ THE HEADER OF `composerLayout.ts` BEFORE CHANGING WHAT IS PASSED
   * HERE. It must be a quantity THE DRAG CANNOT CHANGE. A sum of the list's
   * height and the field's height is the same number and is NOT the same
   * thing: both move with the drag, so a maximum built on them moves while the
   * control is in use, which cost UI-7 three review rounds. The container is a
   * `flex: 1` child of the panel's column — the drag redistributes space inside
   * it and never alters it.
   *
   * Optional and 0-until-measured, so the component stands on its own: with no
   * measurement the range is the pre-UI-8 `[96, 220]`.
   */
  availableHeight?: number;
  /**
   * Bumped by the panel after a SUCCESSFUL submit, and at no other time
   * (#1069 UI-8).
   *
   * A failed submit must NOT reset: the text is still in the box and still
   * being worked on, and taking the user's chosen size away while they are
   * fixing a send failure is a second thing gone wrong. Success is the moment
   * the message stops being theirs to edit, which is exactly when the big box
   * has stopped being useful — without this, the first long paste makes a
   * screen-filling composer permanent and every later reply is typed into it.
   *
   * A signal rather than a callback because the height is this component's
   * state: the panel knows WHEN, the composer knows WHAT.
   */
  heightResetSignal?: number;

  /**
   * Called when a composer TEXT FIELD takes or loses focus (defect 3,
   * 2026-09-15). The host uses it to stand its scroll allowance down; see the
   * long note beside `composerFieldFocused` for why a caret in a sticky box
   * makes that necessary and why the field cannot fix it alone.
   *
   * Optional, and only the DOCKED panel has any use for it: the overlay is a
   * `Modal` with a scroller of its own, where nothing of the request is behind
   * it to scroll.
   */
  onFieldFocusChange?: (focused: boolean) => void;

  /**
   * Files chosen for this message but not yet sent (#1069 Story 4.1, AC 2).
   *
   * Owned by the PANEL, not by the composer, for the same reason `body` is: the
   * panel is what survives a failed send and has to hand the message back
   * intact (AC 13). A composer that owned them would lose them on any remount.
   *
   * Defaulted so every existing call site, and every test that predates
   * attachments, compiles and renders unchanged.
   */
  stagedFiles?: readonly StagedFile[];
  /**
   * The last send on this thread was CANCELLED and may have committed anyway
   * (owner decision, code review run 3, 2026-09-13). Shows a line on the strip;
   * see `MAY_ALREADY_BE_SENT`.
   */
  stagedMayAlreadyBeSent?: boolean;
  /**
   * EDIT shape only (#1530 review, C5): the message being edited holds files.
   * The server lets such an edit save an EMPTY caption (its C4: a message
   * that holds or gains files may have no text), so Save must not stay dark
   * over a cleared caption on a message with pictures.
   */
  editedMessageHoldsFiles?: boolean;
  /** Remove one staged file, BY ID (two files may share a name). */
  onRemoveStagedFile?: (id: string) => void;
  /** Open the platform picker. Absent means the paperclip stays inert. */
  onPickFiles?: () => void;
  /**
   * The editor's image resolver (#1530): bytes in, a staged-inline reference
   * out, or a refusal with the ceilings' own copy. Absent means the editor's
   * image actions are refused with a sentence rather than crashing.
   */
  onResolveImage?: ResolveInlineImage;
  /**
   * Bumped by the host when the editor must start again from `body` (#1530):
   * an edit begins or is cancelled, a compose is discarded. A send that
   * worked already bumps `heightResetSignal`, which remounts it too.
   */
  bodySeed?: number;
}

export const Composer: React.FC<Props> = ({
  mode,
  internal,
  canSeeInternal,
  title,
  onChangeTitle,
  body,
  onChangeBody,
  submitting,
  onStartCompose,
  onCancelCompose,
  onSubmit,
  onCancelEdit,
  panelOpen = true,
  availableHeight = 0,
  heightResetSignal = 0,
  stagedFiles = EMPTY_STAGED_FILES,
  stagedMayAlreadyBeSent,
  editedMessageHoldsFiles = false,
  onRemoveStagedFile,
  onPickFiles,
  onFieldFocusChange,
  onResolveImage,
  bodySeed = 0,
}) => {
  // Read per render, not once at module load: `Platform.OS` is swapped by tests
  // to exercise the web branch, and a module-level constant freezes at import.
  const isWeb = Platform.OS === 'web';
  const composing = mode === 'compose';
  const editing = mode === 'edit';

  /**
   * What the user DRAGGED the field to, or `null` while they have not.
   *
   * `null` is not "96" — it is the difference between auto-grow and a decision.
   * Once this holds a number the field stops resizing itself for the rest of
   * the panel session, because a box that moves under you while you type is
   * worse than one that is the wrong size.
   *
   * Stored ALREADY CLAMPED, against the maximum in force when the user asked
   * for it — and re-clamped on the way out by `pinnedHeight` below, because
   * UI-8's maximum can move (a taller panel, a rotated device). The two
   * together are what stop a squeezed drag banking a height it was never
   * granted: nothing bigger than the ceiling at the time of the gesture ever
   * enters state, so a later re-clamp can only ever give the user back a size
   * they really chose. Storing the RAW request instead — the shape UI-7's first
   * cut used — is how an unbounded request made while squeezed materialised
   * later on a panel that had grown.
   */
  const [draggedHeight, setDraggedHeight] = useState<number | null>(null);

  /**
   * The field's rendered height, measured. 0 until the first layout.
   *
   * NOT a layout input — nothing is derived from it. It is what a gesture
   * STARTS FROM, so a drag or an arrow key that begins on a field auto-grow has
   * already taken to 180 continues from 180 rather than snapping to 96.
   */
  const [fieldHeight, setFieldHeight] = useState(0);

  // Every time the panel opens. Not on unmount — in the docked presentation the
  // panel really does unmount and state goes with it, but as an overlay it does
  // not, and "resets on open" has to be true in both.
  useEffect(() => {
    if (panelOpen) {
      setDraggedHeight(null);
      // `fieldHeight` MUST reset with it. Clearing only the pinned height is
      // half a reset: `currentHeight` falls back to `fieldHeight` the instant
      // `draggedHeight` goes null, so a stale 220 from the previous session
      // survives and the first ArrowDown grows the box by 108px. Round 1's
      // defect, reached through the retained measurement instead of the bound.
      setFieldHeight(0);
    }
  }, [panelOpen]);

  /**
   * And every time composing ENDS. `idle` renders no field at all, so a height
   * kept across it belongs to a message that no longer exists: cancel a
   * compose, start a new one, and the box would come back at the previous
   * message's height with auto-grow still switched off — a size the user chose
   * for other text, which they now have to undo before the new message can
   * grow. The panel never closed, so the `panelOpen` reset above does not
   * cover this.
   */
  useEffect(() => {
    if (mode === 'idle') {
      setDraggedHeight(null);
      setFieldHeight(0);
    }
  }, [mode]);

  /**
   * And every time a message is actually SENT (#1069 UI-8).
   *
   * ⚠️ SUCCESS ONLY. The panel bumps `heightResetSignal` on the code paths that
   * reach the provider's confirmation and on no others, so a failed send leaves
   * the box exactly as the user left it — the text is still in it and still
   * being worked on, and shrinking it while they retry takes away the size they
   * chose to read it in.
   *
   * This is what makes the full-height drag safe to offer at all. Without it,
   * the first long paste makes a screen-filling composer PERMANENT for the rest
   * of the panel session, and every one-line reply after it is typed into a box
   * that has swallowed the conversation.
   *
   * Same pair of resets as the two effects above, deliberately: "as if the
   * panel had just been opened" is one meaning, and three spellings of it would
   * drift. It runs on mount too, where both writes are no-ops.
   */
  useEffect(() => {
    setDraggedHeight(null);
    setFieldHeight(0);
  }, [heightResetSignal]);

  /**
   * THE TWO CEILINGS, BOTH FROM THE ONE MEASUREMENT, AND THE ORDER BETWEEN
   * THEM IS GUARANTEED BY `composerAutoGrowMaxHeight` ITSELF.
   *
   * `dragMaxHeight` is what the handle, the keyboard and the ARIA range use.
   * `autoGrowMaxHeight` is the `maxHeight` an UNDRAGGED field carries, so
   * typing still stops at 220 on any panel with room for it. It is a `Math.min`
   * of the other, so it can never sit above it — see `composerLayout.ts`.
   */
  const dragMaxHeight = composerMaxHeight(availableHeight);
  const autoGrowMaxHeight = composerAutoGrowMaxHeight(availableHeight);

  /**
   * The dragged height AS IT CAN BE HONOURED RIGHT NOW.
   *
   * Re-clamped rather than read straight out of state because the maximum
   * moves with the container: a height chosen on a tall panel must not render
   * taller than a short one, and `aria-valuenow` must not exceed a
   * `aria-valuemax` that has since come down. Idempotent, so this costs
   * nothing when the container has not moved.
   */
  const pinnedHeight =
    draggedHeight === null
      ? null
      : clampComposerHeight(draggedHeight, availableHeight);

  // `fieldHeight` is 0 until the first layout, never nullish, so there is no
  // third operand to reach: `getHeight` falls back from 0 itself, because 0 is
  // a measurement state, not a height.
  const currentHeight = pinnedHeight ?? fieldHeight;
  /**
   * ⚠️ `announcedHeight` IS BACK, WITH THE ANNOUNCEMENT (#1069 Story 7.2,
   * AC 10). The 2026-09-15 note that stood here said to bring it back only
   * with the widget contract; D4 brings the widget contract back.
   *
   * It exists for one reason: `aria-valuenow`. Unclamped, a single layout
   * reporting 500 published `aria-valuenow: 500` against `aria-valuemax: 220`,
   * an invalid range for the `separator` pattern — round 2 of UI-7's review
   * fixed that on the max and it moved to the now, which is why BOTH ends are
   * clamped here rather than one.
   *
   * `|| COMPOSER_MIN_HEIGHT` for the same reason `getHeight` has it: 0 is a
   * measurement state, not a height, and announcing 0 against a minimum of 96
   * is the inverted range in its other spelling. `composerMaxHeight` floors at
   * `COMPOSER_MIN_HEIGHT`, so the range cannot invert on a panel shorter than
   * the minimum either.
   */
  const announcedHeight = clampComposerHeight(
    currentHeight || COMPOSER_MIN_HEIGHT,
    availableHeight,
  );

  // A ref so the pointer and touch listeners never need re-subscribing mid-drag.
  // Written in an effect rather than in the render body: React 19's rule is not
  // to touch `ref.current` while rendering, and a discarded or replayed render
  // would tear it — which the keyboard handler then reads.
  const heightRef = useRef(currentHeight);
  useEffect(() => {
    heightRef.current = currentHeight;
  }, [currentHeight]);

  /**
   * The container height the NEXT gesture will clamp against, held in a ref for
   * exactly the reason `heightRef` is.
   *
   * If `resizeTo` closed over `availableHeight` directly it would change
   * identity on every measurement — which re-runs the web listener effect and
   * rebuilds the `PanResponder`. Both are destructive MID-DRAG: the effect's
   * teardown treats an interrupted drag as a cancel and reverts it, and a
   * rebuilt responder drops the gesture outright. A resize of the browser
   * window during a drag is rare; silently undoing the user's drag when it
   * happens is not a trade worth making for one closure.
   */
  const availableHeightRef = useRef(availableHeight);
  useEffect(() => {
    availableHeightRef.current = availableHeight;
  }, [availableHeight]);

  /**
   * THE ONLY WAY A HEIGHT ENTERS STATE, and therefore the only place the clamp
   * has to be applied. Web pointer, native touch and the keyboard all arrive
   * here, which is what makes "one ceiling" structural rather than a comment.
   */
  const resizeTo = useCallback((next: number) => {
    setDraggedHeight(clampComposerHeight(next, availableHeightRef.current));
  }, []);

  /**
   * The height a resize STARTS FROM, for all three inputs — pointer, touch and
   * keyboard alike.
   *
   * The keyboard used to read `heightRef.current || COMPOSER_MIN_HEIGHT`
   * inline, which was this expression written twice. That duplication was also
   * a coverage hole: the gesture helpers are the only other consumers, and
   * neither is reachable from a component test (the web listeners attach to a
   * ref that is not a DOM node under the RN test renderer, and
   * `PanResponder.create` is mocked to ignore its config), so replacing this
   * body with a constant broke nothing any test could see. Routing the keyboard
   * through it puts the one shared meaning under the one set of assertions that
   * can reach it.
   *
   * `|| COMPOSER_MIN_HEIGHT` because 0 is a MEASUREMENT STATE, not a height: it
   * means the field has not laid out yet, and a gesture starting from 0 would
   * jump the box to its minimum on the first pixel of travel.
   */
  const getHeight = useCallback(
    () => heightRef.current || COMPOSER_MIN_HEIGHT,
    [],
  );

  const onFieldLayout = useCallback((event: LayoutChangeEvent) => {
    const next = Math.round(event.nativeEvent.layout.height);
    // Guarded, not because React would loop on an identical value, but because
    // a fractional layout can otherwise oscillate between two roundings.
    setFieldHeight(prev => (prev === next ? prev : next));
  }, []);

  const handleRef = useRef<View | null>(null);

  /**
   * `mode` IS THE DEPENDENCY, and it is doing real work despite not appearing
   * in the body. The handle only exists in the composing modes, so `handleRef`
   * is null in `idle`; without `mode` here every other dependency is stable and
   * the effect would run exactly once, on mount — so a composer that mounted
   * idle (the thread list) and then started composing would render a handle
   * with no pointer listeners on it and no web drag at all.
   *
   * There is no `mode !== 'idle'` early return, because the `addEventListener`
   * check below already is one: a null ref cannot be subscribed to. A second
   * guard saying the same thing in different words is the kind of redundancy
   * that survives its own mutation.
   */
  useEffect(() => {
    if (!isWeb) return undefined;
    const node = handleRef.current as unknown as ComposerPointerTarget | null;
    if (!node?.addEventListener) return undefined;
    return attachComposerResizeDrag(node, getHeight, resizeTo);
  }, [isWeb, mode, getHeight, resizeTo]);

  /**
   * Native touch drag. Created once — the responder reads the live height
   * through `getHeight`, so it never needs rebuilding, and rebuilding it
   * mid-gesture would drop the drag.
   */
  const panResponder = useMemo(
    () => PanResponder.create(createComposerDragResponder(getHeight, resizeTo)),
    [getHeight, resizeTo],
  );

  /**
   * Focus on the two text fields (Story 5.4, AC 12). One flag per input, so
   * focusing the subject never paints the body. COLOUR ONLY: the border steps
   * `inputBorder` → `textSecondary`, no ring and no shadow, matching the grip.
   */
  const [subjectFocused, setSubjectFocused] = useState(false);
  const [bodyFocused, setBodyFocused] = useState(false);
  // A handle that unmounts while focused never fires blur, so without this the
  // grip would come back focus-styled the next time it appeared. The two
  // fields have the same hole (code review 2026-09-11): Back drops the mode to
  // `idle` and unmounts the body; Send drops `compose` to `reply` and unmounts
  // the subject. Neither fires `onBlur`, so the flags are cleared here.
  useEffect(() => {
    if (mode === 'idle') {
      setSubjectFocused(false);
      setBodyFocused(false);
    }
    if (mode !== 'compose') setSubjectFocused(false);
  }, [mode]);

  /**
   * TELL THE HOST WHEN A COMPOSER FIELD HOLDS FOCUS (defect 3, 2026-09-15).
   *
   * ⚠️ WHY A LAYOUT FEATURE OF ANOTHER SCREEN DEPENDS ON THIS FLAG.
   *
   * On web the docked panel and the request share ONE scroll view (spec
   * 2026-09-07), and `RequestDetail` puts `scroll-padding-bottom` on it equal
   * to the sticky footer band, so Tab-into-the-last-field lands above the band
   * rather than under it. That allowance applies to EVERY scroll-into-view in
   * the scroller — including the caret's.
   *
   * The composer's caret sits at the bottom edge of the box, which is inside
   * that band, so on every keystroke the browser asks the scroller to reveal
   * it. The panel is `position: sticky`, so scrolling moves it not at all, the
   * caret is still in the band, and it asks again on the next keystroke: the
   * request content slides under the panel a line at a time until the scroller
   * runs out. Measured in the running app 2026-09-15 — 40 characters moved the
   * page 220px over 14 scroll events, and zeroing the allowance made the same
   * 40 characters move it 0px over 0.
   *
   * ⚠️ `scroll-margin-bottom` ON THE FIELD DOES NOT FIX THIS, and the failed
   * attempt is worth recording: a CARET scroll reveals the text position, not
   * the element, so the element's scroll-margin is simply not consulted. It
   * measured as having no effect at all. The allowance itself has to stand
   * down, which only its owner can do.
   *
   * Derived from the two flags rather than wired into the four handlers, so it
   * inherits the `mode` cleanup above: `Back` and `Send` unmount a focused
   * field WITHOUT firing `onBlur`, and a flag left true would leave the host's
   * allowance switched off for the rest of the session.
   */
  const composerFieldFocused = subjectFocused || bodyFocused;
  const onFieldFocusChangeRef = useRef(onFieldFocusChange);
  useEffect(() => {
    onFieldFocusChangeRef.current = onFieldFocusChange;
  }, [onFieldFocusChange]);
  useEffect(() => {
    onFieldFocusChangeRef.current?.(composerFieldFocused);
  }, [composerFieldFocused]);
  // And on unmount, because the docked panel really does unmount (the overlay
  // does not) and a host left holding `true` never gets its allowance back.
  useEffect(
    () => () => {
      onFieldFocusChangeRef.current?.(false);
    },
    [],
  );

  /**
   * Keyboard operation, ON THE MESSAGE FIELD rather than on the grip (owner,
   * 2026-09-15 — see `webHandleProps` for why the grip left the tab order).
   * Arrow direction still matches the drag: the handle is on the TOP edge, so
   * UP makes the field taller.
   *
   * A keyboard resize is as deliberate as a drag, so it switches auto-grow off
   * on the same terms.
   *
   * ⚠️ ALT IS REQUIRED, AND THE POLARITY IS THE OPPOSITE OF WHAT IT WAS. On a
   * focusable separator a BARE arrow was correct and a modifier meant "this is
   * the browser's shortcut, keep out". On a text field a bare arrow belongs to
   * the caret and may never be taken, so the modifier is what distinguishes a
   * resize from typing. Ctrl and Cmd are still refused: Ctrl+Arrow is Mission
   * Control on macOS and Cmd+Arrow is start/end-of-text in every field.
   *
   * Alt+Arrow costs macOS its by-paragraph caret move inside this one box.
   * That was chosen as the least-bad collision — in a composer bounded at 220px
   * a paragraph jump is close to a line jump — and `preventDefault` stops the
   * caret moving as well as resizing.
   */
  const resizeByKey = useCallback(
    (event: ResizeGripKeyEvent, requireAlt: boolean) => {
      /**
       * ⚠️ THE SAME KEYS, TWO MODIFIER POLICIES, AND THE POLARITY IS OPPOSITE
       * BY DESIGN (#1069 Story 7.2, D4).
       *
       * On the FIELD, `Alt` is required: a bare arrow belongs to the caret and
       * may never be taken. On the GRIP, which is a focusable `separator`, a
       * bare arrow is the by-the-book window-splitter gesture and a modifier
       * means "this is the browser's shortcut, keep out". Ctrl and Cmd are
       * refused on both — Ctrl+Arrow is Mission Control on macOS and Cmd+Arrow
       * is start/end-of-text.
       *
       * One body, because the arithmetic below is the part that must not
       * diverge between the two entry points.
       */
      if (requireAlt ? !event.altKey : event.altKey) return;
      if (event.ctrlKey || event.metaKey) return;

      const step = event.shiftKey
        ? COMPOSER_RESIZE_STEP * COMPOSER_RESIZE_COARSE_MULTIPLIER
        : COMPOSER_RESIZE_STEP;
      // Steps from the height ON SCREEN — including one auto-grow has just
      // produced — which is the same starting point both drags take, through
      // the same function.
      const current = getHeight();

      let next: number | null = null;
      if (event.key === 'ArrowUp') next = current + step;
      else if (event.key === 'ArrowDown') next = current - step;
      // The MEASURED ceiling, not the fallback constant. Home means "as far as
      // this control goes", and a keyboard user who could not reach the height
      // a mouse user can drag to would be given a smaller feature by the same
      // control.
      else if (event.key === 'Home') next = dragMaxHeight;
      else if (event.key === 'End') next = COMPOSER_MIN_HEIGHT;
      if (next === null) return;

      event.preventDefault?.();
      resizeTo(next);
    },
    [dragMaxHeight, getHeight, resizeTo],
  );

  /** The message field's own resize keys: `Alt`+Arrow/Home/End (D4 keeps it). */
  const onResizeKey = useCallback(
    (event: ResizeGripKeyEvent) => resizeByKey(event, true),
    [resizeByKey],
  );

  /** The grip's: bare Arrow/Shift+Arrow/Home/End, the window-splitter gesture. */
  const onGripResizeKey = useCallback(
    (event: ResizeGripKeyEvent) => resizeByKey(event, false),
    [resizeByKey],
  );

  /**
   * ⚠️ THE GRIP IS A WIDGET AGAIN (owner, 2026-09-17 — #1069 Story 7.2, D4).
   *
   * The comment that stood here said the opposite, and it was right about its
   * own moment. The grip had been the ARIA Authoring Practices **Window
   * Splitter** — `role="separator"`, `tabIndex: 0`, a live `aria-value*` range
   * and arrow keys — and lost all of it on 2026-09-15 because it sat BETWEEN
   * the subject and the message box, so Tab out of the subject landed on the
   * grip instead of on the field. The comment also recorded the alternative it
   * rejected: *"Reordering the grip after the field was the other candidate and
   * was rejected: it puts focus order out of step with visual order, trading
   * 2.1.1 for 2.4.3."*
   *
   * That is exactly the trade the owner has now instructed (story C1/D4), and
   * it is recorded rather than quietly reversed: **the grip is focusable and
   * sits AFTER the message field in DOM order while being DRAWN at the card's
   * top edge.** Visual order and focus order disagree here, knowingly, and
   * strict SC 2.4.3 agreement is what was spent.
   *
   * ⚠️ AND THE REASON IT MUST, not merely may. Under D1 the grip is drawn on
   * the CARD's top edge, above the notice and the subject. In DOM order at its
   * visual position it would be the FIRST stop in the whole composer — Tab out
   * of the notice would hit it — which is worse than the defect that removed
   * the tab stop in the first place. `position: 'absolute'` is what lets the
   * two orders come apart.
   *
   * ⚠️ The whole widget contract goes together or none of it does:
   * `separator` is a STRUCTURAL role until it is focusable, and `aria-value*`
   * is not supported on the non-focusable form. `ResizeGrip` emits the set as
   * one object for exactly that reason; see `announcedHeight` for the value.
   *
   * `Alt`+Arrow ON THE FIELD STAYS (D4). It costs one retained handler and it
   * means SC 2.1.1 has a path that does not depend on this reordering being
   * right — and removing it would make a typing user tab away to resize the box
   * they are typing in.
   */

  /**
   * Keyboard focus on the grip, tracked here for the same reason the two
   * fields' focus is: a handle that unmounts while focused never fires blur,
   * so the flag is cleared when composing ends (the `mode` effect above).
   */
  const [gripFocused, setGripFocused] = useState(false);
  useEffect(() => {
    if (mode === 'idle') setGripFocused(false);
  }, [mode]);

  /**
   * The native half of the keyboard range (#1069 Story 7.2, Task 5, closing
   * deferred-work `:308-309`). `increment` is a swipe up in VoiceOver and a
   * volume-up in TalkBack; it makes the box TALLER, matching both the drag
   * (the handle is on a top edge) and `ArrowUp` on web.
   */
  const onGripAccessibilityAdjust = useCallback(
    (action: 'increment' | 'decrement') => {
      const current = getHeight();
      resizeTo(
        action === 'increment'
          ? current + COMPOSER_RESIZE_STEP
          : current - COMPOSER_RESIZE_STEP,
      );
    },
    [getHeight, resizeTo],
  );

  /**
   * The grip, DRAWN ON THE CARD'S TOP EDGE and mounted last (D1, D4).
   *
   * It is rendered from the card below, not from `content`'s composing branch,
   * and it is therefore gated on `mode !== 'idle'` by hand: the card renders in
   * `idle` too, and `ComposerResize.test.tsx` pins that an idle composer has no
   * grip. That gate is the whole of what the old "UNCONDITIONAL, because of
   * where it sits" note used to get for free.
   *
   * It carries no visible text, so there is no WCAG 2.5.3 "Label in Name"
   * obligation; the accessible name is the whole name.
   */
  const resizeHandle = (
    <ResizeGrip
      direction="horizontal"
      testIDPrefix="correspondence-composer-resize"
      name="Resize message box"
      value={announcedHeight}
      min={COMPOSER_MIN_HEIGHT}
      max={dragMaxHeight}
      valueText={`${announcedHeight} pixels tall`}
      focusVisible={gripFocused}
      style={styles.resizeHandle}
      hitSlop={resizeHitSlop}
      handleRef={handleRef}
      onKeyDown={onGripResizeKey}
      onFocus={() => setGripFocused(true)}
      onBlur={() => setGripFocused(false)}
      panHandlers={panResponder.panHandlers}
      onAccessibilityAdjust={onGripAccessibilityAdjust}
    />
  );

  /**
   * TWO AUDIENCES, TWO VOICES — deliberately NOT one neutral sentence that is
   * true for everybody.
   *
   * The tempting simplification is a single string both viewers can read. It
   * would be safe for the customer and it would delete the one thing the
   * internal user relies on: the explicit statement of who can read what they
   * are about to write. Posting to the wrong space is the one mistake this
   * feature cannot undo, so the internal voice NAMES the space and states the
   * consequence, and must stay exactly as it is.
   *
   * The external voice names no space and describes the audience INCLUSIVELY
   * — the reader is inside it, not a third party being referred to. To
   * somebody who can see only one space, "shared" and "the customer" are both
   * descriptions of a room they are being told they stand outside of.
   */
  const consequence = !canSeeInternal
    ? 'Everyone on this request can see this message.'
    : internal
    ? 'Internal only. The customer cannot see this.'
    : 'The customer will see this message.';

  /**
   * Composing states the destination. For a viewer with one space there is no
   * destination to choose, so naming it is pure disclosure.
   */
  const destination = !canSeeInternal
    ? 'New conversation'
    : `New conversation in the ${internal ? 'internal' : 'shared'} space`;

  /**
   * THE INTERNAL TREATMENT — the padlock. (It was "padlock, primary-coloured
   * text, tinted dock" until 2026-09-08; the text is `textPrimary` and the
   * dock is the panel grey now, so the icon is the whole of it.)
   *
   * Gated on the VOICE, not on `internal` alone. From the panel the pair
   * (`internal: true`, `canSeeInternal: false`) is unreachable, because
   * `activeSpace` is forced to `'shared'` for a viewer who cannot see the
   * other one — but `Composer` takes two independent booleans and nothing in
   * its own signature says so. Keyed on `internal` alone, that pair would
   * paint a PADLOCK over "Everyone on this request can see this message.",
   * which is the confidentiality claim inverted. Costs one `&&` to make the
   * component safe on its own terms rather than on its caller's.
   */
  const internalVoice = canSeeInternal && internal;

  /**
   * What may be sent (Story 1.3, AC 34). Composing needs BOTH a subject and a
   * message: the first message is required (D1), so a title-only send is no
   * longer legal and the hint below says which half is missing. Replying and
   * editing need the message. A body over the server's limit disables the
   * button with a hint rather than being refused by the server first; the
   * server measures the cleaned text and is the control.
   */
  const hasTitle = !!title.trim();
  /**
   * #1530: the body is HTML now, so "has text" and "too long" are the
   * server's text measure (`messageTextLength`: plain text, entities decoded,
   * an image counted as `[image]`, which is what makes a lone picture
   * sendable), and the markup has its own ceiling.
   */
  /**
   * ⚠️ MEASURED ON THE WIRE SHAPE, NOT THE EDITOR'S (#1530 review, C3). The
   * native editor shows a data URI for a staged or held picture and may not
   * have re-emitted the ref form yet, so `body` can carry `data:` srcs that
   * are tens of kilobytes each. The panel maps them back to refs before it
   * sends (`wireDraft`); the gate maps the same way, with the editors' own
   * exact-map reverse, or a message the server would take is refused here
   * as too much formatting.
   */
  const wireBody = useMemo(
    () => rewriteHtmlPreviewSrcsToPendingRefs(body),
    [body],
  );
  const textLength = messageTextLength(wireBody);
  const hasBody = textLength > 0;
  const isTooLong = textLength > MAX_MESSAGE_LENGTH;
  const tooMuchFormatting = isMessageHtmlTooLarge(wireBody);
  /**
   * The one "can send" truth includes the editor's own work in progress
   * (spec): an image handed to the resolver and not yet inserted is a message
   * that is not finished. Subscribed, so the button follows the insert.
   */
  const insertInFlight = useSyncExternalStore(
    subscribeInlineImageUploadsInFlight,
    hasInlineImageUploadsInFlight,
    hasInlineImageUploadsInFlight,
  );
  /**
   * ⚠️ FILES ALONE ARE A MESSAGE, BUT ONLY IN THE REPLY SHAPE (#1069 Story 4.1,
   * AC 6, C4, FR2). A photo of a nameplate with no caption is a message; before
   * this story the only way to say anything was to type.
   *
   * STARTING a conversation is unchanged: Story 1.3's D1 requires the subject
   * AND the first message, and the paperclip does not render in compose mode at
   * all. `composing ? hasTitle && hasBody` is therefore left exactly as it was,
   * so a first message cannot become sendable on a file even if one somehow
   * reached the props.
   *
   * The length ceiling still applies either way: a file does not buy a way past
   * it, because a caption is still text.
   */
  /**
   * ⚠️ TWO DIFFERENT QUESTIONS, AND THEY WERE ONE VARIABLE (code review
   * 2026-09-13).
   *
   * `hasFiles` is a FACT — are any staged? It is what suppresses
   * `MESSAGE_REQUIRED_HINT` (AC 6).
   *
   * ⚠️ THAT SUPPRESSION IS NOT REACHABLE IN THE PRODUCT, AND THIS COMMENT USED
   * TO CLAIM IT WAS "shape-independent" (code review run 3, 2026-09-13; the
   * story retracted the claim at run 2 and the comment it was describing was
   * never corrected). The hint renders under `composing &&`, `composerMode` is
   * `'compose'` only when `openThreadId == null`, and the panel supplies
   * `EMPTY_STAGED` whenever `openThreadId` is null — so `hasFiles` is always
   * false in the one branch that reads it. The split below is still worth
   * keeping (it is what stops a staged file from lighting up Save in the EDIT
   * shape, which is reachable and was a real bug), but `Composer.test.tsx`'s
   * `{mode:'compose', stagedFiles:[aFile()]}` case is asserting a prop
   * combination the panel cannot produce. Left in place deliberately as a
   * guard on the component's own contract, NOT read as evidence about the
   * product.
   *
   * `filesMakeItSendable` is a RULE — do they make this submittable? Only in
   * the reply shape. The ternary below special-cased `composing` alone, so
   * `hasBody || hasFiles` also covered EDIT: stage a file, edit your own
   * message, clear the text, and Save lit up — then did nothing on press,
   * because the panel's `post()` excludes editing from `carriesFiles` and
   * returns at the empty body. An enabled primary button that no-ops is worse
   * than a disabled one. Editing changes the words; this story does not add
   * files to that path.
   */
  const stripFiles = useMemo(
    () => stagedFiles.filter(f => !f.inline),
    [stagedFiles],
  );
  /**
   * ⚠️ ONLY WHAT WOULD GO (#1530 review, C2). An inline image is staged the
   * moment the resolver reads it and PRUNED only once the editor has shown it
   * and then dropped it (`reconcileInlineStaged`), so the staged list can
   * hold a picture the body no longer refers to, which `outgoingMessage`
   * would not send. Counting it here lit Send over an empty message and hid
   * `MESSAGE_REQUIRED_HINT`. The strip's files always go; an inline file
   * counts only while the body refers to it.
   */
  const referencedInline = useMemo(
    () => referencedInlineIds(wireBody),
    [wireBody],
  );
  const hasFiles =
    stripFiles.length > 0 ||
    stagedFiles.some(f => f.inline === true && referencedInline.has(f.id));
  const filesMakeItSendable = hasFiles && !composing && !editing;
  /**
   * #1530 review, C5: an edit may save an empty caption when the message
   * holds files or gains them (the server's C4). A gained inline image is
   * already `hasBody` (it counts as `[image]`); held files are the prop.
   */
  const editKeepsFiles = editing && editedMessageHoldsFiles;
  const canSubmit =
    (composing
      ? hasTitle && hasBody
      : hasBody || filesMakeItSendable || editKeepsFiles) &&
    !isTooLong &&
    !tooMuchFormatting &&
    !insertInFlight;
  const disabled = !canSubmit || submitting;

  /**
   * The web send chord (#1530): Ctrl/⌘+Enter inside the editor goes through
   * the SAME gate as the button, so it cannot send what the button would
   * refuse. Read through a ref so the editor is not rebuilt on every render.
   */
  const submitIfAllowedRef = useRef<() => void>(() => undefined);
  submitIfAllowedRef.current = () => {
    if (!disabled) onSubmit();
  };
  const onSubmitShortcut = useCallback(() => {
    submitIfAllowedRef.current();
  }, []);

  const content =
    mode === 'idle' ? (
      <TouchableOpacity
        onPress={onStartCompose}
        style={styles.newThreadBtn}
        testID="correspondence-new-thread"
        /**
         * WCAG 2.5.3 Label in Name: the accessible name must CONTAIN the
         * visible text, or a voice-control user saying what they can read
         * cannot activate the control. This label was once frozen at "Start a
         * new discussion" to protect WDIO page objects that do not exist, and
         * that freeze broke conformance the moment the visible text became
         * "New conversation". Label and copy move together from here.
         */
        accessibilityLabel="New conversation"
      >
        <Plus size={16} color={colors.textPrimary} />
        <Text style={styles.newThreadBtnText}>New conversation</Text>
      </TouchableOpacity>
    ) : (
      <>
        {editing ? (
          /* The strip that says what the box is doing now (D2). It sits above
             the notice so the audience reminder still reads directly over the
             field: an edit is still a message the customer may read. */
          <View
            style={styles.editingStrip}
            testID="correspondence-editing-strip"
          >
            <Text style={styles.editingStripText}>{EDITING_STRIP_TEXT}</Text>
          </View>
        ) : null}
        <View style={styles.notice}>
          {internalVoice ? (
            <Lock size={NOTICE_ICON_SIZE} color={colors.textPrimary} />
          ) : (
            <Users size={NOTICE_ICON_SIZE} color={colors.textPrimary} />
          )}
          {/* testID so a test can prove the notice RENDERED before asserting
              what it does not say. An absence assertion over a dock that drew
              no notice at all passes for the wrong reason. */}
          <Text
            style={[
              styles.noticeText,
              !internalVoice && styles.noticeTextShared,
            ]}
            testID="correspondence-composer-notice"
          >
            {composing ? destination : consequence}
          </Text>
        </View>
        {/* Composing states BOTH the destination and its consequence. For an
            internal viewer, naming the space alone is not a safety cue to
            somebody who has not internalised what "shared" costs; for an
            external one the destination line carries no space at all, so the
            consequence is the whole of it. */}
        {composing ? (
          /* Its own testID, because in COMPOSE mode this is the line that
             carries the fix. The notice above it says only "New conversation" to an
             external viewer — a string that can never fail a vocabulary check
             — so a test that proves "the notice rendered" proves nothing about
             the sentence that actually describes the audience. */
          <Text
            style={[
              styles.noticeSubText,
              !internalVoice && styles.noticeTextShared,
            ]}
            testID="correspondence-composer-consequence"
          >
            {consequence}
          </Text>
        ) : null}

        {composing ? (
          <TextInput
            value={title}
            onChangeText={onChangeTitle}
            placeholder="What is this about?"
            placeholderTextColor={colors.textMuted}
            style={[
              styles.titleInput,
              subjectFocused ? styles.inputFocused : null,
            ]}
            onFocus={() => setSubjectFocused(true)}
            onBlur={() => setSubjectFocused(false)}
            testID="correspondence-new-thread-title"
            maxLength={MAX_TITLE_LENGTH}
            autoFocus
          />
        ) : null}

        {/**
         * The frozen matrix requires an empty title to be rejected "with a
         * reason". A disabled button with no explanation is exactly the case
         * that row was written against, so say why — but only once the user has
         * written something to send, since an untouched composer is not an
         * error, it is just empty. The mirror case (a subject with no message,
         * Story 1.3 D1) gets its own line for the same reason.
         */}
        {composing && !hasTitle && hasBody ? (
          <Text style={styles.hint} testID="correspondence-title-required">
            Add a subject above to send this message.
          </Text>
        ) : null}
        {/*
          ⚠️ SUPPRESSED WHILE FILES ARE STAGED (AC 6). The hint says a message
          is required; with a file attached one is not, and a hint that
          contradicts an enabled button is worse than no hint.
        */}
        {composing && hasTitle && !hasBody && !hasFiles ? (
          <Text style={styles.hint} testID="correspondence-message-required">
            {MESSAGE_REQUIRED_HINT}
          </Text>
        ) : null}
        {isTooLong ? (
          <Text style={styles.hint} testID="correspondence-body-too-long">
            {BODY_TOO_LONG_HINT}
          </Text>
        ) : null}
        {tooMuchFormatting ? (
          <Text
            style={styles.hint}
            testID="correspondence-body-too-much-formatting"
          >
            {BODY_TOO_MUCH_FORMATTING_HINT}
          </Text>
        ) : null}

        {/* ⚠️ THE WRAPPER THAT HELD THE GRIP AND THE FIELD TOGETHER IS GONE
            (#1069 Story 7.2, D1). Its entire job was to defeat the dock's
            `gap: 8` between those two, so the grip did not float 8px above the
            box and read as a separate control. The grip is no longer here — it
            is drawn on the card's top edge — so there is no pair left to group,
            and the field takes the dock's gap directly, exactly as the wrapper
            did around it. */}

        {/**
         * Sized for a PASTED MULTI-PARAGRAPH EMAIL, which is the motivating
         * case: a generous minimum so a real message is visible while it is
         * written, and a maximum past which it scrolls instead of eating the
         * thread above it. It never clips.
         *
         * The bounds come from STATE, not the stylesheet (#1069 UI-7). Two
         * regimes, and the difference between them is the whole point:
         *
         *  - undragged, it auto-grows between 96 and 220 — EXACTLY what the
         *    stylesheet gave before this feature existed;
         *  - dragged, it is PINNED. `height`, `minHeight` and `maxHeight` are
         *    all the same number so neither platform's auto-sizing can
         *    reinterpret it, and the field scrolls instead of moving under the
         *    user mid-sentence.
         *
         * UI-8: the DRAG can now go past 220, all the way to the measured
         * container, while typing still stops at 220. The two ceilings differ
         * on purpose and can never CROSS — `autoGrowMaxHeight` is a
         * `Math.min` of `dragMaxHeight`, so a field auto-grow has taken to
         * the top is always somewhere the drag is allowed to be, which is the
         * property whose absence made UI-7's first ArrowUp drop the box 88px.
         */}
        {/* #1530: the house editor in the box. The HOST View owns everything
            the `TextInput` used to own that is not the editor's job: the two
            height regimes above (`fillHeight` makes the editor fill whatever
            this box is), the focus border and the host's focus report (DOM
            focus bubbles through React on web; native's WebView never
            reports, and only the docked web host listens), the Alt+Arrow
            resize keys (a React `onKeyDown` on the host, which ProseMirror
            lets bubble because it binds no Alt chord), and the layout report.
            `overflow: hidden` because the native editor's own minimum is
            taller than `COMPOSER_MIN_HEIGHT`. */}
        <View
          style={[
            styles.editorHost,
            pinnedHeight === null
              ? {
                  minHeight: COMPOSER_MIN_HEIGHT,
                  maxHeight: autoGrowMaxHeight,
                }
              : {
                  height: pinnedHeight,
                  minHeight: pinnedHeight,
                  maxHeight: pinnedHeight,
                },
            bodyFocused ? styles.inputFocused : null,
          ]}
          onLayout={onFieldLayout}
          testID="correspondence-composer-field"
          /* Focus bubbles through React on web (react-native-web's View
             forwards `onFocus`/`onBlur`); on native a View never reports it
             and the props are inert, so they are not platform-gated. The
             resize keys ARE: native has no Alt key. */
          {...({
            onFocus: () => setBodyFocused(true),
            onBlur: () => setBodyFocused(false),
            ...(isWeb ? { onKeyDown: onResizeKey } : {}),
          } as Record<string, unknown>)}
        >
          <TextAreaEditor
            /* Remounted when the host says the text is new (an edit begins
               or ends, a send worked, a compose is discarded) and when the
               shape changes: the editor is seeded ONCE from `body`. */
            key={`${mode}:${heightResetSignal}:${bodySeed}`}
            initialHtml={body}
            onHtmlChange={onChangeBody}
            placeholder="Write a message"
            onResolveImage={onResolveImage ?? refuseInlineImage}
            inlineImageFieldKey={COMPOSER_INLINE_IMAGE_FIELD_KEY}
            fillHeight
            /* One row at rest (owner walk, 2026-10-01): the full set wrapped
               to three rows in the panel. Web only; native keeps its own
               one-row compact toolbar and ignores the prop. */
            toolbarLayout="expandable"
            /* The Teams message set (owner, 2026-10-05): Quote and Code
               snippet buttons in; the superscript, subscript, alignment and
               inline-code BUTTONS out (the `<code>` tag itself is still
               allowed and still pastes). Web shapes the expanded row and
               drops the sub / sup / align / font-family extensions; native
               shapes its compact flyouts. */
            toolbarSet="message"
            onSubmitShortcut={isWeb ? onSubmitShortcut : undefined}
            editable={!submitting}
          />
          {/*
              ⚠️ READ-ONLY WHILE SENDING, NOT CLEARED AND NOT FADED (AC 12).
              Clearing optimistically would lose file handles the failure path
              cannot re-derive; AC 13 promises the message and its files are
              still there afterwards. No opacity change either: fading says
              "disabled" about content the user is about to get back. The
              editor is read-only through `editable` (review 2026-10-01); the
              blocker stays as well, because `setEditable(false)` leaves the
              toolbar and the native WebView's tap-to-arm handlers reachable
              (`TextIntakeModal`'s shape).
            */}
          {submitting ? (
            <View
              style={styles.editorBlocker}
              pointerEvents="auto"
              testID="correspondence-composer-blocker"
            />
          ) : null}
        </View>

        {/*
          ⚠️ BETWEEN THE FIELD AND THE ACTIONS ROW, and neither side of that is
          arbitrary. Not inside the `TextInput`: React Native cannot reliably
          inline non-text views into a text input across web, iOS and Android.
          Not below the actions row: the panel has a logged hazard where Post
          gets pushed below the dock edge (~line 1205), and Post must stay the
          last thing before that edge.
        */}
        {/* ⚠️ NOT IN THE EDIT OR COMPOSE SHAPES (code review 2026-09-13). The
            paperclip renders in neither — a first message cannot carry files
            and an edit changes the words — so a strip in those shapes advertises
            an attachment that cannot be sent from there. The files are not
            discarded, they are just not shown: they belong to the thread and
            come back when the composer does. */}
        {/* #1530: an INLINE image is in the box, not in the strip; it still
            counts toward the paperclip's ceiling below. */}
        {editing || composing ? (
          /* #1530 review, C1: no strip in these shapes (the pictures are in
             the box), but an unconfirmed files edit or create still owes the
             caution. Same testID as the strip's, so the panel's pins read one
             surface. */
          stagedMayAlreadyBeSent ? (
            <Text
              style={styles.hint}
              testID="correspondence-staged-may-be-sent"
            >
              {MAY_ALREADY_BE_SENT}
            </Text>
          ) : null
        ) : (
          <StagedFileStrip
            files={stripFiles}
            onRemove={onRemoveStagedFile ?? noop}
            sending={submitting}
            mayAlreadyBeSent={stagedMayAlreadyBeSent}
          />
        )}

        <View style={styles.actions}>
          {composing ? (
            <TouchableOpacity
              onPress={onCancelCompose}
              style={styles.ghostBtn}
              testID="correspondence-cancel-compose"
              disabled={submitting}
            >
              <Text style={styles.ghostBtnText}>Cancel</Text>
            </TouchableOpacity>
          ) : editing ? (
            <TouchableOpacity
              onPress={onCancelEdit}
              style={styles.ghostBtn}
              testID="correspondence-cancel-edit"
              accessibilityLabel="Cancel editing"
              disabled={submitting}
            >
              <Text style={styles.ghostBtnText}>Cancel</Text>
            </TouchableOpacity>
          ) : (
            /* Attachments on a FIRST message are explicitly out of this spec,
               so the control belongs to the reply shape only. */
            <TouchableOpacity
              style={styles.attachBtn}
              testID="correspondence-attach"
              accessibilityLabel="Attach a file"
              onPress={onPickFiles}
              /*
                Disabled at the ceiling (AC 4) AND while sending (AC 12). The
                count refusal still exists for the multi-select that arrives
                over the line in one go, because disabling the control cannot
                refuse WITH A REASON and AC 4 requires the reason.
              */
              disabled={
                !onPickFiles ||
                submitting ||
                stagedFiles.length >= MAX_FILES_PER_MESSAGE
              }
            >
              <Paperclip size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={onSubmit}
            style={[styles.primaryBtn, disabled && styles.primaryBtnDisabled]}
            disabled={disabled}
            accessibilityLabel={
              composing
                ? 'Send message'
                : editing
                ? 'Save changes'
                : 'Post message'
            }
            testID={
              composing
                ? 'correspondence-create-thread'
                : editing
                ? 'correspondence-save-edit'
                : 'correspondence-post'
            }
          >
            <Text style={styles.primaryBtnText}>
              {/* ⚠️ "Sending" ONLY IN THE REPLY SHAPE (AC 12). Compose and edit
                  keep their own verbs; this story changes the post path. */}
              {composing
                ? 'Send'
                : editing
                ? 'Save'
                : submitting
                ? 'Sending'
                : 'Post'}
            </Text>
          </TouchableOpacity>
        </View>
      </>
    );

  const dock = (
    <View
      style={[
        styles.dock,
        mode === 'idle'
          ? styles.dockIdle
          : internalVoice
          ? styles.dockInternal
          : styles.dockShared,
      ]}
      testID="correspondence-composer-dock"
    >
      {content}
      {/**
       * ⚠️ LAST CHILD, DRAWN FIRST (D1 + D4, and the two halves are one edit).
       *
       * It is absolutely positioned on the card's top edge, so where it is
       * MOUNTED and where it is DRAWN are independent — which is the whole
       * mechanism by which it can be the last tab stop in the composer while
       * being the topmost thing in it. See the widget-contract note above.
       *
       * Gated on `idle` by hand: this card renders in `idle` (it is the "New
       * conversation" button's shell), and an idle composer has no field to
       * resize.
       */}
      {mode === 'idle' ? null : resizeHandle}
    </View>
  );

  /**
   * ⚠️ THE DOCK NO LONGER AVOIDS THE KEYBOARD ITSELF, AND MUST NOT START
   * AGAIN (defect 2, 2026-09-15).
   *
   * It used to wrap itself in react-native's `KeyboardAvoidingView` with
   * `behavior: 'padding'` on iOS and `undefined` on Android, the Android half
   * resting on the manifest's `adjustResize`. That reasoning was wrong in one
   * specific way: the panel renders inside a `Modal`, and **an RN `Modal` is
   * its own window and does not inherit `windowSoftInputMode`**, so Android
   * had no avoidance at all and the keyboard covered whichever field had
   * focus. On iOS the padding stacked against the panel root's own
   * `insets.bottom`.
   *
   * Avoidance now belongs to `CorrespondencePanel`, which is the component
   * that can actually give the space back: the message list is the panel
   * column's one `flex: 1` child, so shortening the column makes the LIST
   * yield and the dock ride up. The composer could only ever have padded
   * itself, which moves nothing. See `PanelKeyboardColumn` there.
   *
   * A second avoider here would double-count the keyboard.
   */
  return dock;
};

const styles = StyleSheet.create({
  dock: {
    borderTopWidth: 1,
    padding: 12,
    gap: 8,
    /**
     * The dock may not outgrow the region it sits in.
     *
     * The drag bounds the FIELD, but the dock is the field plus its notice
     * line, grip, actions row and padding — about 126px more. So a field
     * dragged to the region's full height makes the dock overflow by that much:
     * the message list has already reached zero, the grip stops following the
     * pointer, and the Post button is pushed below the bottom of the panel.
     *
     * Capped here rather than subtracted from the maximum, deliberately. The
     * chrome differs between compose and reply and moves when the notice wraps,
     * so subtracting it means either MODELLING a layout — which
     * `responsive-ui.md` records failing three times in this area — or taking a
     * SECOND measurement, which is the thing UI-7 spent three review rounds
     * removing. The style system already knows the real number; let it enforce
     * it. The announced maximum stays slightly optimistic, and nothing leaves
     * the screen.
     */
    maxHeight: '100%',
  },
  // Collapsed: no coloured wash, because there is nothing to warn about until
  // there is something to send.
  dockIdle: {
    borderTopColor: colors.borderPanel,
    backgroundColor: colors.backgroundPrimary,
  },
  /**
   * ONE GREY, WHICHEVER AUDIENCE (owner, 2026-09-08). The composing dock is the
   * panel's quiet band — `surfacePanelMuted` under a `borderPanel` edge — for
   * both spaces, matching the rest of the panel after Story 5.1.
   *
   * It used to say the audience by colour here: an orange top edge on an
   * `surfaceAccentMuted` wash for internal, a yellow top edge on white for
   * shared (UI-8 had already stripped the shared WASH because a yellow fill
   * read as a warning on a panel where nothing is wrong). The owner then took
   * the colour off the panel's other chrome the same day — the tabs, the top
   * border — and asked this section to follow. The audience is still said, in
   * words, twice, directly on this band: the notice line ("New conversation in
   * the internal space") and the consequence line beneath it. Colour was only
   * ever the third telling.
   *
   * Two named styles are kept rather than one, so a future per-space
   * difference is a one-line change and not a re-wiring.
   */
  dockInternal: {
    borderTopColor: colors.borderPanel,
    backgroundColor: colors.surfacePanelMuted,
  },
  dockShared: {
    borderTopColor: colors.borderPanel,
    backgroundColor: colors.surfacePanelMuted,
  },

  notice: { flexDirection: 'row', alignItems: 'center', gap: NOTICE_GAP },
  /**
   * PLAIN BODY TEXT, `textPrimary`, for both variants (owner, 2026-09-08 —
   * "black text"). It was `textSecondary`, and before that the space colours
   * (`primary` for internal, `textOnWarningMuted` for shared), each chosen as
   * the dark half of a pair with a wash that UI-8 removed; a coloured sentence
   * with no ground reads as a mistake, and `primary` on white is 2.84:1 and
   * fails outright. The icon was the last place the space colour lived and it
   * now takes `textPrimary` too, so the notice is one colour end to end.
   *
   * `textPrimary` on `surfacePanelMuted` is 11.36:1 (measured, Story 5.1).
   */
  noticeText: { fontSize: 11, fontWeight: '600', color: colors.textPrimary },
  noticeSubText: {
    fontSize: 11,
    color: colors.textPrimary,
    // Aligns under the notice text, past the icon and its gap, rather than
    // carrying the sum as a magic number that silently drifts if either moves.
    marginLeft: NOTICE_ICON_SIZE + NOTICE_GAP,
  },
  hint: { fontSize: 11, color: colors.textOnWarningMuted },
  /** The edit strip (D2): quiet, informational, above the notice. */
  editingStrip: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: colors.surfacePanelMuted,
    alignSelf: 'flex-start',
  },
  editingStripText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  /**
   * Emptied, not deleted: the shared variant no longer recolours the WORDS.
   * Both spaces now read in `textSecondary` and differ only by their icon and
   * their top border. Kept as a named style so the two call sites that apply it
   * keep their shape, and so re-colouring one space is a one-line change rather
   * than a re-wiring.
   */
  noticeTextShared: {},

  /**
   * The idle "New conversation" affordance: the Visual Template's DASHED
   * OUTLINE button in its black variant, sized to its content and centred
   * (owner, 2026-09-08, against the Figma button sheet). Dashed `textPrimary`
   * border on a white ground, `textPrimary` label and icon — the house black,
   * deliberately not `#000` (see the note on `textPrimary` in `colors.ts`).
   *
   * It was a full-width dashed ORANGE outline, then briefly a solid black pill
   * the same day; the owner's reference shows the outline shape with the black
   * colourway, which is this. `alignSelf: 'center'` is what stops it
   * stretching: the dock lays its children out full-width, and a button with
   * no `alignSelf` inherits that. Radius 8 matches the other dashed "add"
   * controls in the product (`AddScopeControl.addButton`, the device-list add
   * rows), which are the same Figma component in its orange colourway.
   */
  newThreadBtn: {
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.textPrimary,
    backgroundColor: colors.backgroundPrimary,
  },
  newThreadBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.textPrimary,
  },

  titleInput: {
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: colors.textPrimary,
    backgroundColor: colors.backgroundPrimary,
  },
  /**
   * NO `minHeight` / `maxHeight` here any more. Both bounds are the same two
   * numbers a dragged field is pinned to, and keeping them in one place — the
   * `style` array on the field itself — is what stops the stylesheet and the
   * drag becoming two owners of one height again.
   */
  editorHost: {
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 6,
    backgroundColor: colors.backgroundPrimary,
    overflow: 'hidden',
    position: 'relative',
  },
  editorBlocker: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  /**
   * Focus on either field (Story 5.4, AC 12): a grey step on the border,
   * `inputBorder` (#ddd) → `textSecondary` (#666). Not the brand orange focus
   * token — that was the last orange in the panel after Story 5.1 — and not
   * `formControlFocusRing`: a ring on a field inside a dock that already has
   * a hover grip reads as two controls lighting up. Width stays 1px.
   */
  inputFocused: {
    borderColor: colors.textSecondary,
  },

  /**
   * WHERE THE HANDLE SITS, and nothing else: `ResizeGrip` owns the 24px target
   * and the pill, this owns the placement (#1069 Story 7.2, D1/D3).
   *
   * ⚠️ THE TARGET IS LIFTED HALF OF **ITSELF**, NOT HALF A PILL (#1069 Story
   * 8.6, C1). `ResizeGrip.handleHorizontal` CENTRES the 4pt pill inside its
   * 24pt target, so the pill's centre line sits `GRIP_TARGET / 2` below the
   * target's own top edge. Lifting the target by `GRIP_TARGET / 2` therefore
   * puts the pill's centre line on the card's top border, to within the 1pt of
   * `borderTopWidth`: both CSS and Yoga resolve an absolute child's insets
   * against the parent's PADDING box, so the centre line lands on that border's
   * inner edge rather than on its centre line. Sub-pixel, and not measured in a
   * browser (AC 1 asks for that and it was not taken), so "on the border" here
   * is a derivation with a known half-point of slack, not an observation.
   *
   * ⚠️ THE CENTRING THAT MAKES THIS TRUE LIVES IN ANOTHER FILE.
   * `ResizeGrip.handleHorizontal`'s `justifyContent: 'center'`
   * (`ResizeGrip.tsx:273-277`) is the premise; flip it to `'flex-start'` and
   * this offset silently throws the pill 12pt ABOVE the border while every
   * assertion in `ComposerResize.test.tsx` stays green. That mutant is caught
   * only by `ResizeGrip.test.tsx`, in a different suite. Predicate here, input
   * there, and no test where they meet: the shape `correspondence.md` records.
   *
   * This line used to read `-(GRIP_PILL_THICKNESS / 2)`, i.e. `-2`, carried
   * over from the OLD rule where the pill was pinned to the BOTTOM of its
   * target and pulled half its own height down across the FIELD's border. With
   * the pill centred, `-2` puts the pill's centre line at `+10`, ten points
   * BELOW the card's border, which is what the owner reported on 2026-09-21.
   * The comment that used to sit here said the opposite and the test pinned the
   * wrong number with the same wrong reason; both are corrected together.
   *
   * `CorrespondenceDock.tsx:560` is this same rule on the other axis,
   * `left: -(GRIP_TARGET / 2)`. One rule seen twice, not two literals that
   * happen to agree.
   *
   * ── The twelve points it gains upstairs are accepted (D2) ─────────────────
   *
   * ⚠️ D2 REASONED ABOUT ONE UPSTAIRS NEIGHBOUR AND THERE ARE THREE (code
   * review 2026-09-22). Whatever sits above the card takes a 12pt band:
   *
   *   `MessageList`    its own 12pt content inset (`MessageList.tsx:1124`,
   *                    `bodyContent`). At the resting scroll position the band
   *                    is inert padding with no bubble under it; scrolled up, a
   *                    bubble's bottom edge reaches it. This is the case D2
   *                    weighed and accepted.
   *   `ThreadList`     rendered instead of the list whenever no thread is open
   *                    (`CorrespondencePanel.tsx:2943`), which INCLUDES
   *                    `mode === 'compose'`, where this grip is drawn. It has
   *                    NO inset of its own, on purpose (Story 2.4 AC 8), so the
   *                    band lands on a live pressable row. See the note below.
   *   `errorBar`       the unsent-message line (`CorrespondencePanel.tsx:2949`)
   *                    when a send has failed. A `View` and a `Text`, inert, so
   *                    no tap is at risk; but it carries a `warning`
   *                    `borderBottomWidth`, so in that one state the pill is
   *                    centred on a 2pt double rule rather than on one border.
   *
   * ⚠️ AND THE HARM IS PLATFORM-EXCLUSIVE, WHICH IS WHY IT IS TOLERABLE. On
   * native the overhanging half is not hit-tested at all (see `resizeHitSlop`),
   * so it steals nothing from any of the three; on web it is, so a press in
   * that band over a `ThreadList` row resizes the composer instead of opening
   * the conversation, and a wheel there does not scroll the list. Neither is
   * fixable by a line in this file. The candidates, for whoever takes it, are
   * on the ledger: reserve the 12pt with a `marginTop` on the card, narrow the
   * handle to the pill's width, or accept and say so.
   *
   * The hit SLOP still may not go upward (see `resizeHitSlop`): unchanged.
   *
   * `position: 'absolute'` is load-bearing twice: it lets the grip be drawn at
   * the top while mounted last (D4's DOM order), and it keeps the handle out of
   * the card's flex column, so `gap: 8` does not open a hole where it sits and
   * the composing chrome below does not move by a pixel.
   *
   * `zIndex: 1` orders the pill over the card's own border and over `content`,
   * its siblings INSIDE this card. ⚠️ It is not what wins the overlap with the
   * message list: that is a different subtree and a different stacking context,
   * and a child cannot raise its parent. The grip draws over the list because
   * the composer card is the LATER SIBLING (`CorrespondencePanel.tsx:2944` then
   * `:2962`). On Android cross-sibling order is elevation rather than JSX order
   * (`DiscussionToggle.tsx:28-30`), which is harmless only while nothing in
   * `MessageList` carries `elevation`; if something ever does, this breaks
   * silently and the fix is not here.
   *
   * ⚠️ NOTHING IN THE ANCESTOR CHAIN MAY CLIP. Half the target and half the
   * pill are outside this card, so an `overflow: 'hidden'` added anywhere above
   * cuts the pill in half with no failing test. `CorrespondenceDock` had to
   * remove exactly that for its own grip (`CorrespondenceDock.tsx:556-560`);
   * checked clean here on 2026-09-22 (`styles.clip` is the full-height panel
   * body and the overhang is vertical, well inside it).
   */
  resizeHandle: {
    position: 'absolute',
    top: -(GRIP_TARGET / 2),
    left: 0,
    right: 0,
    zIndex: 1,
  },
  /**
   * ⚠️ `resizeGrip` IS GONE FROM THIS FILE, and so is the note that said the
   * grip's focus state would "come back with the tab stop" (2026-09-15). Both
   * did come back, in `ResizeGrip.tsx`, where the dock's grip reads the same
   * rules: rest `inputBorderNeutral`, drag `textSecondary`, focus
   * `textPrimary`. A second copy here is the divergence this story deleted.
   */

  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  attachBtn: {
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.borderPanel,
    backgroundColor: colors.backgroundPrimary,
  },
  /**
   * BLACK, not brand orange (#1069 UI-8). Send/Post is the one irreversible
   * action on this surface, and black carries more contrast against white text
   * than the brand orange does — the orange is spent on the confidentiality
   * cues around it, which is where the panel wants the eye.
   */
  primaryBtn: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 6,
    backgroundColor: colors.actionSolidBlack,
  },
  primaryBtnDisabled: { opacity: 0.4 },
  primaryBtnText: { fontSize: 13, fontWeight: '700', color: colors.textLight },
  ghostBtn: { paddingHorizontal: 14, paddingVertical: 9 },
  ghostBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
  },
});
